import React, {useRef, useState} from "react";
import moment from "moment";
import {Button, Col, DatePicker, Form, Grid, Input, Modal, Row, Select} from "antd";
import { PSA_INSTANCE } from './Constants';
import { Alert } from "antd";

const {useBreakpoint} = Grid;


// Ensures options are always in { label, value } format expected by Ant Design Select
const normalizeOptions = (options) => {
  if (!options || !Array.isArray(options)) return [];
  return options
    .filter((opt) => opt !== null && opt !== undefined)
    .map((opt) =>
      typeof opt === "object" ? opt : {label: String(opt), value: opt}
    );
};

const PTO_POLICY_OPTIONS = [
  {label: "Standard", value: "Standard"},
  {label: "Hours Cap", value: "Hours Cap"},
  {label: "Multiplier Cap", value: "Multiplier Cap"},
  {label: "No Accrual", value: "No Accrual"},
];


const CreateUpdateEmployeeModal = ({visible, onClose, onSave, manageData, employee = null}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [ptoPolicy, setPtoPolicy] = useState("");
  const [psaInstances, setPsaInstances] = useState([]);
  const [primaryPsaInstance, setPrimaryPsaInstance] = useState(undefined);
  const [ptoModifier, setPtoModifier] = useState("");
  const [ptoHoursOverride, setPtoHoursOverride] = useState("");
  const screens = useBreakpoint();
  const skipPolicyEffectRef = useRef(false);
  const isCreate = !employee || !employee.id;
  const [apiError, setApiError] = useState(null);

  const [selectedEmploymentStatus, setSelectedEmploymentStatus] = useState("");

  // Populate / reset form whenever the modal opens or the employee changes
  React.useEffect(() => {
    if (!visible) return;
    if (employee) {
      // Prevent the ptoPolicy effect from overwriting the employee's saved modifier
      skipPolicyEffectRef.current = true;
      const nameParts = (employee.fullName || "").trim().split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      setPtoPolicy(employee.ptoPolicy || "");
      setPtoModifier(employee.ptoModifier != null ? String(employee.ptoModifier) : "");
      setPtoHoursOverride(employee.ptoHoursOverride != null ? String(employee.ptoHoursOverride) : "");
      setSelectedEmploymentStatus(employee.employmentStatus || "");
      setPsaInstances(employee.psaInstances || []);
      setPrimaryPsaInstance(employee.primaryPsaInstance || "");

      const managerId = manageData.managers.find(o => o.e === employee.manager)?.i;
      const careerCoachId = manageData.managers.find(o => o.e === employee.careerCoach)?.i;

      form.setFieldsValue({
        firstName,
        lastName,
        email: employee.email,
        title: employee.title,
        level: employee.level,
        practiceName: employee.practiceName,
        capability: employee.capability,
        locationName: employee.locationName,
        employmentStatus: employee.employmentStatus,
        contractorRate: employee.contractorRate,
        managerId: managerId,
        careerCoachId: careerCoachId,
        startDate: employee.startDate ? moment(employee.startDate) : null,
        termDate: employee.termDate ? moment(employee.termDate) : null,
        effectiveDate: null,
      });
    } else {
      form.resetFields();
      setPtoPolicy("Standard");
      setPtoModifier("");
      setPtoHoursOverride("");
      setSelectedEmploymentStatus("");
      setPsaInstances(["CREDERA"]);
      setPrimaryPsaInstance("CREDERA");
    }
  }, [visible, employee]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    // Skip auto-fill when loading existing employee data
    if (skipPolicyEffectRef.current) {
      skipPolicyEffectRef.current = false;
      return;
    }
    switch (ptoPolicy) {
      case "Hours Cap":
        setPtoModifier("40");
        break;
      case "Multiplier Cap":
        setPtoModifier("1.5");
        break;
      case "Standard":
        setPtoModifier("");
        break;
      case "No Accrual":
        setPtoModifier("");
        setPtoHoursOverride("");
        break;
      default:
        break;
    }
  }, [ptoPolicy]);

  const isModifierDisabled = ptoPolicy === "Standard" || ptoPolicy === "No Accrual";
  const isHoursOverrideDisabled = ptoPolicy === "No Accrual";

  const isContractorRateDisabled = selectedEmploymentStatus?.toLowerCase() !== "contractor";


  React.useEffect(() => {
    if (selectedEmploymentStatus !== "contractor") {
      form.setFieldValue("contractorRate", undefined);
    }
  }, [selectedEmploymentStatus, form]);

  // Modal width: 1000px on xl+, 88vw on lg, 92vw on md, 96vw on sm/xs
  const modalWidth = screens.xl
    ? 1000
    : screens.lg
      ? "88vw"
      : screens.md
        ? "92vw"
        : "96vw";

  // Col span: 4 cols on lg+, 2 cols on md/sm, 1 col on xs
  const colProps = {xs: 24, sm: 12, md: 6, lg: 6};

  const managerOptions = (manageData.managers || []).map((emp) => ({
    label: emp.e,
    value: emp.i,
  }));

  const handleSave = async () => {
    try {
      setApiError("");
      const values = await form.validateFields();
      setLoading(true);
      await onSave({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        title: values.title,
        level: values.level,
        practiceName: values.practiceName,
        capability: values.capability,
        locationName: values.locationName,
        employmentStatus: values.employmentStatus,
        contractorRate: values.contractorRate,
        managerId: values.managerId,
        careerCoachId: values.careerCoachId,
        startDate: values.startDate,
        termDate: values.termDate,
        effectiveDate: values.effectiveDate,
        psaInstances,
        primaryPsaInstance,
        ptoPolicy,
        ptoModifier,
        ptoHoursOverride,
      });
      form.resetFields();
      setPtoPolicy("");
      setPtoModifier("");
      setPtoHoursOverride("");
      setPsaInstances(["CREDERA"]);
      setPrimaryPsaInstance("CREDERA");
      onClose();
    } catch (e) {
      let errorMessage = "Unable to save employee.";
      try {
        const errorData = await e.json();
        errorMessage = errorData?.message || errorData?.error || "Unable to save employee.";
      } catch(parseError) {
      }
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setPtoPolicy("");
    setPtoModifier("");
    setPtoHoursOverride("");
    onClose();
  };

  return (
    <Modal
      title={employee ? "Update Employee" : "Create New Employee"}
      open={visible}
      onCancel={handleCancel}
      width={modalWidth}
      closable
      maskClosable={false}
      style={{top: 20}}
      bodyStyle={{
        padding: "12px 20px",
        overflowY: "auto",
        maxHeight: "calc(100vh - 160px)",
      }}
      footer={[
        <Button
          key="cancel"
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </Button>,
        <Button
          key={employee ? "save" : "create"}
          type="primary"
          loading={loading}
          onClick={handleSave}
        >
          {employee ? "Save" : "Create"}
        </Button>,
      ]}
    >
      {apiError && (
        <Alert
          type="error"
          message={apiError}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Form form={form} layout="vertical" size="middle">
        {/* Row 1 */}
        <Row gutter={[24, 0]}>
          <Col {...colProps}>
            <Form.Item label="Effective Date" name="effectiveDate">
              <DatePicker
                style={{width: "100%"}}
                placeholder="Select effective date"
                disabled={true}
              />
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{required: true, message: "Please enter a first name"}]}
            >
              <Input placeholder="Enter first name"/>
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{required: true, message: "Please enter a last name"}]}
            >
              <Input placeholder="Enter last name"/>
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                {required: true, message: "Please enter an email"},
                {type: "email", message: "Please enter a valid email"},
              ]}
            >
              <Input placeholder="Enter email address"/>
            </Form.Item>
          </Col>
        </Row>

        {/* Row 2 */}
        <Row gutter={[24, 0]}>
          <Col {...colProps}>
            <Form.Item
              label="Title"
              name="title"
              rules={[{required: true, message: "Please select a title"}]}
            >
              <Select
                placeholder="Select job title"
                options={normalizeOptions(manageData.titles)}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item label="Level" name="level" rules={[{required: true, message: "Please select a level"}]}>
              <Select placeholder="Select level" options={normalizeOptions(manageData.levels)} allowClear/>
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item label="Practice" name="practiceName">
              <Select
                placeholder="Select practice"
                options={normalizeOptions(manageData.practices)}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item label="Capability" name="capability">
              <Select
                placeholder="Select capability"
                options={normalizeOptions(manageData.capabilities)}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 3 */}
        <Row gutter={[24, 0]}>
          <Col {...colProps}>
            <Form.Item label="Location" name="locationName" rules={[{ required: true, message: "Please select a location" }]}>
              <Select
                placeholder="Select location"
                options={normalizeOptions(manageData.locations)}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item label="Manager" name="managerId">
              <Select
                placeholder="Select manager"
                options={managerOptions}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item label="Career Coach" name="careerCoachId">
              <Select
                placeholder="Select career coach"
                options={managerOptions}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item label="Employment Status" name="employmentStatus"
                       rules={[{required: true, message: "Please select an employment status"}]}>
              <Select
                placeholder="Select employment status"
                options={normalizeOptions(manageData.employment_status)}
                allowClear
                onChange={(value) => setSelectedEmploymentStatus(value || "")}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 4 — PTO fields */}
        <Row gutter={[24, 0]}>
          <Col {...colProps}>
            <Form.Item
              label="PTO Policy"
              rules={[{required: true, message: "Please select a PTO Policy"}]}
            >
              <Select
                value={ptoPolicy}
                onChange={setPtoPolicy}

                placeholder="Select PTO policy"
                options={PTO_POLICY_OPTIONS}
              />
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item label="PTO Modifier">
              <Input
                value={ptoModifier}
                onChange={(e) => {
                  const val = e.target.value;
                  if (
                    (ptoPolicy === "Hours Cap" || ptoPolicy === "Multiplier Cap") &&
                    /^\d*\.?\d{0,2}$/.test(val)
                  ) {
                    setPtoModifier(val);
                  } else if (ptoPolicy !== "Hours Cap" && ptoPolicy !== "Multiplier Cap") {
                    setPtoModifier(val);
                  }
                }}
                placeholder={
                  ptoPolicy === "Hours Cap"
                    ? "e.g. 40 or 40.50"
                    : ptoPolicy === "Multiplier Cap"
                      ? "e.g. 1.50"
                      : ""
                }
                disabled={isModifierDisabled}
              />
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item
              label="PTO Hours Override"
              tooltip={
                "Manually set the current monthly PTO hours accrual for this employee, " +
                "overriding the calculated value from the normal accrual schedule. " +
                "Low: 10.00, Mid: 13.33, High: 16.67"
              }
            >
              <Input
                value={ptoHoursOverride}
                onChange={(e) => {
                  const val = e.target.value;
                  if (ptoPolicy !== "No Accrual" && /^\d*\.?\d{0,2}$/.test(val)) {
                    setPtoHoursOverride(val);
                  } else if (ptoPolicy === "No Accrual") {
                    setPtoHoursOverride(val);
                  }
                }}
                placeholder="e.g. 10 or 16.33"
                disabled={isHoursOverrideDisabled}
              />
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item label="Contractor Rate" name="contractorRate">
              <Input
                type="number"
                min={0}
                step={0.01}
                placeholder="Enter contractor rate"
                prefix="$"
                disabled={isContractorRateDisabled}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 5 */}
        <Row gutter={[24, 0]}>
          <Col {...colProps}>
            <Form.Item
              label="Start Date"
              name="startDate"
              rules={[{required: true, message: "Please select a start date"}]}
            >
              <DatePicker style={{width: "100%"}} placeholder="Select start date"/>
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item label="Term Date" name="termDate">
              <DatePicker
                style={{width: "100%"}}
                placeholder="Select term date"
                disabled={isCreate}
              />
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item
              label="Psa Instances"
              tooltip={
                "Indicate to what Projector PSA Instances this employee belongs. " +
                "Keep blank if you do not want to push this Employee to Projector. "
              }
              rules={[{required: false, message: "Please select Psa Instances"}]}
            >
              <Select
                value={psaInstances}
                mode="multiple"
                onChange={setPsaInstances}
                placeholder="Please select Psa Instance(s)"
                options={PSA_INSTANCE}
              />
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item
              label="Primary Psa Instance"
              tooltip={
                "Indicate what Projector PSA Instance this employee enters their time and PTO. " +
                "Keep blank if this Employee is not on Projector. "
              }
              rules={[{required: false, message: "Please select Psa Instances"}]}
            >
              <Select
                value={primaryPsaInstance}
                onChange={setPrimaryPsaInstance}
                allowClear
                placeholder="Select Primary Psa Instance"
                options={PSA_INSTANCE}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default CreateUpdateEmployeeModal;



